import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Plus, Search, Filter, Grid, List, 
  Calendar, Users, DollarSign, Target, Clock, 
  CheckCircle, AlertTriangle, TrendingUp, MapPin,
  ChevronRight, MoreHorizontal, Star, Eye, Edit,
  Trash2, Download, Share2, Bookmark, MessageSquare
} from 'lucide-react';
import { sampleProjects, projectCategories, projectStatuses, projectPriorities } from '../../data/sampleProjects';

const ProjectList = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navigate = useNavigate();

  const projects = sampleProjects;

  const filters = [
    { id: 'all', label: 'All Projects', count: projects.length },
    { id: 'in-progress', label: 'In Progress', count: projects.filter(p => p.status === 'In Progress').length },
    { id: 'planning', label: 'Planning', count: projects.filter(p => p.status === 'Planning').length },
    { id: 'completed', label: 'Completed', count: projects.filter(p => p.status === 'Completed').length },
    { id: 'at-risk', label: 'At Risk', count: projects.filter(p => p.status === 'At Risk').length }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'In Progress': return 'bg-blue-100 text-blue-800';
      case 'At Risk': return 'bg-red-100 text-red-800';
      case 'Planning': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-500';
      case 'High': return 'bg-orange-500';
      case 'Medium': return 'bg-yellow-500';
      case 'Low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = selectedFilter === 'all' || 
                         (selectedFilter === 'in-progress' && project.status === 'In Progress') ||
                         (selectedFilter === 'planning' && project.status === 'Planning') ||
                         (selectedFilter === 'completed' && project.status === 'Completed') ||
                         (selectedFilter === 'at-risk' && project.status === 'At Risk');
    return matchesSearch && matchesFilter;
  });

  const ProjectCard = ({ project }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      onClick={() => navigate(`/projex/projects/${project.id}`)}
      className={`p-6 rounded-2xl backdrop-blur-sm border cursor-pointer ${
        isDarkMode 
          ? 'bg-white/10 border-white/20 hover:bg-white/20' 
          : 'bg-white/70 border-gray-200/50 hover:bg-white/90'
      } shadow-lg hover:shadow-xl transition-all duration-300`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {project.name}
            </h3>
            <div className={`w-2 h-2 rounded-full ${getPriorityColor(project.priority)}`}></div>
          </div>
          <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-3`}>
            {project.description}
          </p>
          <div className="flex items-center space-x-4 text-xs">
            <div className={`px-2 py-1 rounded-full ${getStatusColor(project.status)}`}>
              {project.status}
            </div>
            <div className={`flex items-center space-x-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <MapPin className="w-3 h-3" />
              <span>{project.location}</span>
            </div>
            <div className={`flex items-center space-x-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <Calendar className="w-3 h-3" />
              <span>Due {new Date(project.deadline).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button className={`p-2 rounded-lg transition-all ${
            isDarkMode 
              ? 'hover:bg-white/10 text-gray-300' 
              : 'hover:bg-gray-100 text-gray-600'
          }`}>
            <MoreHorizontal className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Progress</span>
          <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>{project.progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-indigo-500 to-purple-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${project.progress}%` }}
          />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            ${(project.budget / 1000000).toFixed(1)}M
          </div>
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Budget</div>
        </div>
        <div className="text-center">
          <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {project.team}
          </div>
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Team</div>
        </div>
        <div className="text-center">
          <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {project.milestones.filter(m => m.completed).length}/{project.milestones.length}
          </div>
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Milestones</div>
        </div>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mb-4">
        {project.tags.map((tag, index) => (
          <span
            key={index}
            className={`px-2 py-1 text-xs rounded-full ${
              isDarkMode 
                ? 'bg-white/10 text-gray-300' 
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Team Members */}
      <div className="flex items-center justify-between">
        <div className="flex -space-x-2">
          {project.teamMembers.slice(0, 3).map((member, index) => (
            <div
              key={index}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white ${
                isDarkMode ? 'bg-indigo-500' : 'bg-gradient-to-r from-indigo-500 to-purple-600'
              }`}
            >
              {member.avatar}
            </div>
          ))}
          {project.teamMembers.length > 3 && (
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
              isDarkMode 
                ? 'bg-white/20 text-gray-300' 
                : 'bg-gray-200 text-gray-600'
            }`}>
              +{project.teamMembers.length - 3}
            </div>
          )}
        </div>
        <button className={`flex items-center space-x-1 px-3 py-1 rounded-lg transition-all ${
          isDarkMode 
            ? 'hover:bg-white/10 text-gray-300' 
            : 'hover:bg-gray-100 text-gray-600'
        }`}>
          <span className="text-sm">View Details</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Project List
            </h1>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Comprehensive project catalog with filtering and search
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/projex/projects/new')}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl shadow-lg"
          >
            <Plus className="w-4 h-4" />
            <span>New Project</span>
          </motion.button>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-6">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border transition-all ${
                isDarkMode 
                  ? 'bg-white/10 border-white/20 text-white placeholder-gray-400' 
                  : 'bg-white/80 border-gray-200 text-gray-900 placeholder-gray-500'
              } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
            />
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-2">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setSelectedFilter(filter.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  selectedFilter === filter.id
                    ? isDarkMode
                      ? 'bg-indigo-500 text-white'
                      : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                    : isDarkMode
                      ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                      : 'bg-white/80 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {filter.label} ({filter.count})
              </button>
            ))}
          </div>

          {/* View Toggle */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all ${
                viewMode === 'grid'
                  ? isDarkMode
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                  : isDarkMode
                    ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                    : 'bg-white/80 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-all ${
                viewMode === 'list'
                  ? isDarkMode
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                  : isDarkMode
                    ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                    : 'bg-white/80 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Projects Grid */}
      <div className={`grid gap-6 ${
        viewMode === 'grid' 
          ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
          : 'grid-cols-1'
      }`}>
        <AnimatePresence>
          {filteredProjects.map((project, index) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </AnimatePresence>
      </div>

      {/* Empty State */}
      {filteredProjects.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <Building2 className={`w-16 h-16 mx-auto mb-4 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-300'
          }`} />
          <h3 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            No projects found
          </h3>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Try adjusting your search or filter criteria
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default ProjectList; 